/**
 * @file Сохранение скачанного файла через браузерный загрузчик.
 *
 * Основной путь — StreamSaver с помощником на нашем домене.
 * Если потоковый путь недоступен, используется ограниченный по памяти Blob.
 * Engine вызывает openDownloadWriter после проверки файла и квоты.
 */
import { withAbort } from '../../services/abort.ts'
import { DownloadError, MEMORY_SAVE_LIMIT } from './model.ts'
import type {
    DownloadMetadata,
    DownloadSource,
    DownloadWriter,
} from './model.ts'
import { streamingSaveAvailable, validateMemorySave } from './savePolicy.ts'

function memoryWriter(name: string): DownloadWriter {
    let chunks: Uint8Array<ArrayBuffer>[] = []
    let length = 0

    return {
        write: async (data) => {
            length += data.byteLength
            if (length > MEMORY_SAVE_LIMIT)
                throw new DownloadError('memoryLimit')

            chunks.push(data)
        },
        close: async () => {
            const url = URL.createObjectURL(
                new Blob(chunks, { type: 'application/octet-stream' }),
            )
            chunks = []
            const link = document.createElement('a')
            link.href = url
            link.download = name
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        },
        abort: async () => {
            chunks = []
            length = 0
        },
    }
}

export async function openDownloadWriter(
    source: DownloadSource,
    metadata: DownloadMetadata,
    signal: AbortSignal,
): Promise<DownloadWriter> {
    signal.throwIfAborted()

    // Запасной способ собирает Blob в памяти. Предел проверяется до чтения
    // содержимого файла, чтобы большой файл не исчерпал память вкладки.
    if (!streamingSaveAvailable()) {
        validateMemorySave(metadata.size)

        return memoryWriter(source.name)
    }

    // Помощник StreamSaver размещён на нашем домене: открытые данные
    // не должны проходить через чужую страницу-посредник.
    const imported = await import('./vendor/StreamSaver.js')
    signal.throwIfAborted()

    const streamSaver = imported.default
    streamSaver.mitm = new URL(
        '/download-bridge/mitm.html',
        window.location.origin,
    ).href

    const writer = streamSaver
        .createWriteStream(source.name, { size: metadata.size })
        .getWriter()
    void writer.closed.catch(() => {})

    if (signal.aborted) {
        await withAbort(
            writer.abort(signal.reason),
            AbortSignal.timeout(5000),
        ).catch(() => {})
        signal.throwIfAborted()
    }

    // close означает передачу данных браузеру, а не подтверждение записи на диск.
    return {
        write: (data) => writer.write(data),
        close: () => writer.close(),
        abort: (reason) => writer.abort(reason),
    }
}
